import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Divider, IconButton, InputAdornment, OutlinedInput, Stack, Tooltip, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { Empty } from "Components/_Shared/Empty";
import { useGetInstruments } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Instrument } from "Infrastructure/ApiZfp/Schema/instrument";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { useInstrumentTypeLabel } from "./useInstrumentTypeLabel";

export type InvestmentInstrument = Pick<
  Instrument,
  | "instrumentID"
  | "name"
  | "isin"
  | "ticker"
  | "isEsg"
  | "minInvestmentHorizon"
  | "srri"
  | "sri"
  | "instrumentType"
  | "instrumentRelations"
  | "customerInstruments"
  | "instrumentClass"
>;

type InstrumentRowProps = {
  readonly instrument: InvestmentInstrument;
  readonly selected: boolean;
  readonly onSelect: (instrument: InvestmentInstrument) => void;
};

const InstrumentRow = ({ instrument, selected, onSelect }: InstrumentRowProps) => {
  const { t } = useResource();
  const getInstrumentTypeLabel = useInstrumentTypeLabel();
  return (
    <Flex alignItems="center" gap={Spacing.M} py={Spacing.S} px={Spacing.S} bgcolor={selected ? "action.selected" : undefined}>
      <Tooltip
        title={
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: Spacing.XS, columnGap: Spacing.L }}>
            <Typography variant="caption" color="grey.300">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Name)}
            </Typography>
            <Typography variant="caption" color="grey.50">
              {instrument.name}
            </Typography>
            <Typography variant="caption" color="grey.300">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Isin)}
            </Typography>
            <Typography variant="caption" color="grey.50">
              {instrument.isin}
            </Typography>
            <Typography variant="caption" color="grey.300">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Symbol)}
            </Typography>
            <Typography variant="caption" color="grey.50">
              {instrument.ticker}
            </Typography>
          </Box>
        }
        arrow
      >
        <IconButton size="small" sx={{ flexShrink: 0 }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={instrument.name}>
          {instrument.name}
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ width: 48, flexShrink: 0, textAlign: "center" }}>
        {instrument.isEsg ? t(Resources.Common.Yes) : t(Resources.Common.No)}
      </Typography>

      <Typography variant="caption" sx={{ width: 64, flexShrink: 0, textAlign: "center" }}>
        {instrument.minInvestmentHorizon}
      </Typography>

      <Typography variant="caption" sx={{ width: 72, flexShrink: 0, textAlign: "center" }}>
        {getInstrumentTypeLabel(instrument.instrumentType)}
      </Typography>

      <Flex sx={{ width: 80, flexShrink: 0 }} justifyContent="center">
        {selected ? (
          <CheckCircleIcon sx={{ color: "primary.main", fontSize: 20 }} />
        ) : (
          <Button variant="text" size="small" onClick={() => onSelect(instrument)} fullWidth>
            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SelectButton)}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export const InvestmentsNsureInstrumentSearch = () => {
  const { t } = useResource();
  const { selection, setSelection } = useContext(NsureSelectionContext);
  const [query, setQuery] = useState("");

  const debouncedQuery = useDebounce(query.trim(), 300);
  const { data, isLoading } = useGetInstruments({ query: debouncedQuery || undefined, orderBy: "name", orderByDirection: "Asc" });

  const instruments: InvestmentInstrument[] = (data?.items ?? []).map((i) => ({
    instrumentID: i.instrumentID,
    isin: i.isin,
    ticker: i.ticker,
    name: i.name,
    isEsg: i.isEsg,
    minInvestmentHorizon: i.minInvestmentHorizon,
    srri: i.srri,
    sri: i.sri,
    instrumentType: i.instrumentType,
    instrumentRelations: i.instrumentRelations ?? [],
    instrumentClass: i.instrumentClass,
    customerInstruments: i.customerInstruments,
  }));

  const handleSelect = (instrument: InvestmentInstrument) => {
    const customerInstrumentPerformance =
      instrument.customerInstruments && instrument.customerInstruments?.length > 0
        ? instrument.customerInstruments[0].expectedReturn
        : null;
    const instrumentClassPerformance = instrument.instrumentClass?.expectedReturn ?? null;
    setSelection({
      id: instrument.instrumentID,
      type: NsureSelectionItemType.Instrument,
      expectedPerformance: customerInstrumentPerformance ?? instrumentClassPerformance,
      name: instrument.name,
      instrumentType: instrument.instrumentType,
      strategyInstruments: [{ id: instrument.instrumentID, name: instrument.name ?? null, participationInPercentage: 100 }],
      strategyInstrumentsEvaluation: [{ id: instrument.instrumentID, name: instrument.name ?? null, participationInPercentage: 100 }],
      isin: instrument.isin,
      ticker: instrument.ticker,
      esg: instrument.isEsg ? 100 : 0,
      minInvestmentHorizon: instrument.minInvestmentHorizon,
      srri: instrument.srri,
      sri: instrument.sri,
      instrumentRelations: instrument.instrumentRelations,
    });
  };

  const selectedInstrumentId = selection?.type === NsureSelectionItemType.Instrument ? selection.id : null;

  return (
    <Stack spacing={Spacing.L} sx={{ flex: 1, minWidth: 0 }}>
      <OutlinedInput
        fullWidth
        placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchPlaceholder)}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon sx={{ color: "text.secondary" }} fontSize="small" />
          </InputAdornment>
        }
        size="small"
      />

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
          <CircularProgress size={24} />
        </Flex>
      ) : instruments.length === 0 ? (
        <Empty
          icon={<SearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
          title={t(
            debouncedQuery
              ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.NoResultsTitle
              : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchEmptyTitle,
          )}
          text={t(
            debouncedQuery
              ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.NoResultsText
              : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentSearch.SearchEmptyText,
          )}
        />
      ) : (
        <Flex flex={1} minWidth={0} flexDirection="column">
          <Flex alignItems="center" gap={Spacing.M} px={Spacing.S} pb={Spacing.S}>
            <Box sx={{ width: 32, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Name)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 48, flexShrink: 0, textAlign: "center" }}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0, textAlign: "center" }}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Horizon)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0, textAlign: "center" }}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Type)}
            </Typography>
            <Box sx={{ width: 95, flexShrink: 0 }} />
          </Flex>
          <Divider />
          {/* Below lg the evaluation panel sits under the list, so the list gives up half its height to keep it in view. */}
          <Stack divider={<Divider />} sx={{ maxHeight: { xs: "30vh", lg: "60vh" }, overflowY: "auto", scrollbarGutter: "stable" }}>
            {instruments.map((instrument) => (
              <InstrumentRow
                key={instrument.instrumentID}
                instrument={instrument}
                selected={selectedInstrumentId === instrument.instrumentID}
                onSelect={handleSelect}
              />
            ))}
          </Stack>
        </Flex>
      )}
    </Stack>
  );
};
